import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-xl border border-slate-200 bg-white p-6", className)}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between">
          {title && <h3 className="text-base font-semibold text-slate-900">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function MetricCard({
  label,
  value,
  delta,
  hint,
}: {
  label: string;
  value: string;
  delta?: { value: string; direction: "up" | "down" | "neutral" };
  hint?: string;
}) {
  const deltaColor =
    delta?.direction === "up"
      ? "text-emerald-600 bg-emerald-50"
      : delta?.direction === "down"
        ? "text-rose-600 bg-rose-50"
        : "text-slate-600 bg-slate-100";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-[12px] font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-[32px] font-bold leading-none text-slate-900">{value}</div>
      <div className="mt-3 flex items-center gap-2">
        {delta && (
          <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold", deltaColor)}>
            {delta.value}
          </span>
        )}
        {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
      </div>
    </div>
  );
}

export function MetricsRow({ items }: { items: { label: string; value: string; delta?: { value: string; direction: "up" | "down" | "neutral" }; hint?: string }[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((m, i) => (
        <MetricCard key={i} {...m} />
      ))}
    </div>
  );
}

export function TodoList({
  items,
}: {
  items: { id: string; title: string; meta: string; severity?: "info" | "warning" | "danger" }[];
}) {
  return (
    <ul className="flex flex-col">
      {items.map((it) => {
        const dot =
          it.severity === "danger"
            ? "bg-rose-500"
            : it.severity === "warning"
              ? "bg-amber-500"
              : "bg-blue-500";
        return (
          <li
            key={it.id}
            className="flex items-start gap-3 border-b border-slate-100 py-3 last:border-0"
          >
            <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", dot)} />
            <div className="flex-1">
              <div className="text-[13px] font-medium text-slate-900">{it.title}</div>
              <div className="text-[11px] text-slate-500">{it.meta}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
